/**
 * <Dropdown> — the shadcn dropdown menu, behind a trigger-plus-children API.
 *
 * Radix owns the open/close state, outside-click and Escape dismissal, the
 * portal, collision-aware placement, focus restoration to the trigger, and —
 * the part the hand-rolled version was missing entirely — the roving-focus
 * keyboard model that `role="menu"` promises: arrow keys, Home/End, typeahead,
 * and Enter/Space to activate. Announcing a menu without that navigation puts
 * screen-reader users into application mode with no working way out.
 *
 * It also renders in a portal now. The previous note said "no portal — keep
 * stacking context local", but that is what made the panel clip inside any
 * `overflow` ancestor, the same defect the Select popover had.
 *
 * Structure is unchanged for callers: <Dropdown.Item>, <Dropdown.Separator>,
 * <Dropdown.Label>, and arbitrary nodes in between.
 */
import type { JSX } from 'react';
import {
  Children,
  Fragment,
  isValidElement,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.js";

type DropdownAlign = "start" | "center" | "end";

export interface DropdownProps {
  /** Button-like node that toggles the panel. Gets `aria-expanded` injected. */
  trigger: ReactElement;
  children?: ReactNode;
  /** Panel alignment relative to the trigger. Default: end. */
  align?: DropdownAlign;
  /** Called whenever the open state changes. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * Radix `DropdownMenuTrigger asChild` clones the trigger element and injects the
 * open/close handlers, `aria-haspopup`/`aria-expanded`, and a ref onto it — via
 * `@radix-ui/react-slot`. Slot cannot forward any of that onto a React
 * `Fragment`: it clones the Fragment (which only honours `key`/`children`) and
 * silently drops every injected prop, leaving the button inert — it never opens.
 *
 * Many call sites wrap the trigger as `trigger={<><Button/></>}` (a habit from
 * JSX that needs a single root), which tripped exactly this trap and shipped
 * dead dropdowns. Unwrap a single-element Fragment to the element Radix can
 * actually wire. Anything else (a real element, or a Fragment with 0/2+ element
 * children Radix could never support anyway) is passed through untouched.
 */
function unwrapFragmentTrigger(trigger: ReactElement): ReactElement {
  if (trigger.type !== Fragment) return trigger;
  const inner = (trigger.props as { children?: ReactNode }).children;
  const only = Children.toArray(inner).filter(isValidElement);
  return only.length === 1 ? (only[0] as ReactElement) : trigger;
}

export function Dropdown({
  trigger,
  children,
  align = "end",
  onOpenChange,
}: DropdownProps): JSX.Element {
  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        {unwrapFragmentTrigger(trigger)}
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align} sideOffset={4}>
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface DropdownItemProps
  // React's own `onSelect` is the text-selection event; radix reuses the name
  // for menu activation, and the item is driven by radix's. Callers wanting
  // activation use `onClick`, which radix fires for pointer and keyboard alike.
  extends Omit<HTMLAttributes<HTMLDivElement>, "onSelect"> {
  /** Mark the "currently selected" variant (accent-dim tint). */
  active?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Skip roving focus and pointer activation. */
  disabled?: boolean;
  /**
   * Whether activating the item closes the menu. Default true, which is what
   * `role="menu"` implies — the hand-rolled version never closed on its own,
   * so every caller either navigated away or left the panel hanging open.
   * Set false for items that toggle state the user wants to keep seeing.
   */
  closeOnSelect?: boolean;
  /** Render the item as its child element — e.g. a router <Link>. */
  asChild?: boolean;
  /**
   * Radix's activation event, for callers that prefer it to onClick — it can
   * preventDefault to hold the menu open. Composed with closeOnSelect rather
   * than replacing it.
   */
  onSelect?: (event: Event) => void;
}

function Item({
  active,
  leading,
  trailing,
  className,
  children,
  disabled,
  closeOnSelect = true,
  asChild,
  onSelect,
  ...rest
}: DropdownItemProps): JSX.Element {
  return (
    <DropdownMenuItem
      aria-current={active || undefined}
      className={className}
      disabled={disabled}
      asChild={asChild}
      onSelect={(event) => {
        if (!closeOnSelect) event.preventDefault();
        onSelect?.(event);
      }}
      {...rest}
    >
      {asChild ? (
        children
      ) : (
        <>
          {leading && <span aria-hidden="true">{leading}</span>}
          <span style={{ flex: 1 }}>{children}</span>
          {trailing && <span aria-hidden="true">{trailing}</span>}
        </>
      )}
    </DropdownMenuItem>
  );
}

function Separator(): JSX.Element {
  return <DropdownMenuSeparator />;
}

function Label({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <DropdownMenuLabel
      className={className}
      {...rest}
    >
      {children}
    </DropdownMenuLabel>
  );
}

/**
 * Multi-select rows. Radix keeps the menu open on a checkbox item by
 * default, which is what a filter list wants.
 */
const CheckboxItem = DropdownMenuCheckboxItem;

/** Single-select rows; wrap them in Dropdown.RadioGroup. */
const RadioGroup = DropdownMenuRadioGroup;
const RadioItem = DropdownMenuRadioItem;

Dropdown.Item = Item;
Dropdown.CheckboxItem = CheckboxItem;
Dropdown.RadioGroup = RadioGroup;
Dropdown.RadioItem = RadioItem;
Dropdown.Separator = Separator;
Dropdown.Label = Label;
